/**
 * EditCollectionCardModal - Edit an owned instance in two phases: edition → details.
 *
 * Shares the Add to Collection steps, without search, remembered fields or the owned-instances list.
 */
import { usePrevious } from '@uidotdev/usehooks';
import { useEffect, useState, type ReactElement } from 'react';
import { toast } from 'sonner';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useUpdateCardMutation } from '@/features/collection/hooks/api';
import useUserSettings from '@/features/settings/hooks/api';
import useCardLookup from '@/hooks/useCardLookup';
import type { EnrichedCollectionCard, Finish } from '@/types/card';

import useAddToCollectionFlow from '../hooks/useAddToCollectionFlow';
import useSetInfoByCode from '../hooks/useSetInfoByCode';
import AddCardDetailsStep from './AddCardDetailsStep';
import AddCardStepNav from './AddCardStepNav';
import { collectionCardToFormData, formDataToUpdateCard, type CardInstanceFormData } from './CardInstanceForm';
import EditionPickerStep from './EditionPickerStep';

export interface EditCollectionCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: EnrichedCollectionCard;
  /** 'edition' opens on the printing picker (Change edition) */
  initialPhase?: 'edition' | 'details';
}

export default function EditCollectionCardModal({
  isOpen,
  onClose,
  card,
  initialPhase = 'details',
}: EditCollectionCardModalProps): ReactElement {
  const updateMutation = useUpdateCardMutation();
  const { settings } = useUserSettings();
  const { cardIndexById, hasFullCard, prefetchCard } = useCardLookup();
  const setInfo = useSetInfoByCode();
  const currentPrinting = cardIndexById.get(card.scryfall_id) ?? null;
  const flow = useAddToCollectionFlow({ isOpen, preSelectedCard: currentPrinting, initialPhase });
  const { phase, selectedCard, selectedPrinting } = flow;

  const [formData, setFormData] = useState<CardInstanceFormData>(() => collectionCardToFormData(card, settings.currency));

  const prevIsOpen = usePrevious(isOpen);
  useEffect(() => {
    if (!isOpen || prevIsOpen) return;
    setFormData(collectionCardToFormData(card, settings.currency));
    updateMutation.reset();
  }, [isOpen, prevIsOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!selectedPrinting) return;
    try {
      // Enrichment joins full card data; load it first so the panel doesn't flash a loading state
      if (!hasFullCard(selectedPrinting.id)) await prefetchCard(selectedPrinting.id);
      await updateMutation.mutateAsync({ cardId: card.id, data: formDataToUpdateCard(selectedPrinting.id, formData) });
      toast.success(`Updated ${selectedPrinting.name} (${selectedPrinting.set.toUpperCase()})`);
      onClose();
    } catch (error) {
      console.error('Failed to update collection card:', error);
    }
  };

  const handleFinishUnavailable = (finish: Finish) => {
    // Keep the saved finish on the saved printing even if Scryfall doesn't list it
    if (selectedPrinting?.id === card.scryfall_id && formData.finish === card.finish) return;
    setFormData((prev) => ({ ...prev, finish }));
  };
  const isPending = updateMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Card" size="xl">
      <AddCardStepNav
        phase={phase}
        availablePhases={flow.availablePhases}
        canVisit={flow.canVisit}
        onNavigate={flow.goToPhase}
        previousPhase={flow.previousPhase}
        onBack={flow.goBack}
        card={selectedCard}
        printing={selectedPrinting}
        printingSetName={selectedPrinting ? setInfo.get(selectedPrinting.set)?.name : undefined}
      />

      {!currentPrinting && (
        <p className="text-ash py-8 text-center text-sm" role="status">
          Card data is still loading…
        </p>
      )}

      {phase === 'edition' && (
        <EditionPickerStep printings={flow.printings} selectedId={selectedPrinting?.id} onSelect={flow.selectPrinting} />
      )}

      {phase === 'details' && selectedPrinting && (
        <>
          <AddCardDetailsStep
            key={selectedPrinting.id}
            printing={selectedPrinting}
            formData={formData}
            onFormChange={setFormData}
            onFinishUnavailable={handleFinishUnavailable}
            disabled={isPending}
            showExistingInstances={false}
            formTitle="Your Copy"
          />

          {updateMutation.isError && (
            <div className="bg-danger/10 text-danger mt-4 rounded-lg px-4 py-3 text-sm" role="alert">
              Failed to save changes. Please try again.
            </div>
          )}

          <div className="border-storm mt-6 flex items-center justify-end gap-3 border-t pt-4">
            <Button type="button" text="Cancel" variant="text" onClick={onClose} disabled={isPending} />
            <Button
              type="button"
              text={isPending ? 'Saving...' : 'Save Changes'}
              color="create"
              onClick={handleSubmit}
              disabled={isPending}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
